import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { ReConsentBanner } from "@/components/ReConsentBanner";
import {
  LayoutDashboard, ScanSearch, Briefcase,
  FlaskConical, Settings, Users, LogOut, ChevronLeft, ChevronRight, Activity, BookOpen, Sun, Moon, Menu, X, CreditCard,
  AlertTriangle, HelpCircle, TrendingUp, ClipboardList, UserCircle, Search, CheckCircle, BarChart3, Landmark, MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { RiskHealthLED } from "@/components/RiskHealthLED";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { PortfolioAssistant } from "@/components/PortfolioAssistant";
import TrialCountdownBanner from "@/components/TrialCountdownBanner";
import BrokerConnectBanner from "@/components/BrokerConnectBanner";
import ClerkAuthControls from "@/components/ClerkAuthControls";

// ═══ Macro Bar ═══
function MacroBar() {
  const [data, setData] = useState(null);
  const [showEarnings, setShowEarnings] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      api.get("/macro/bar").then(({ data: d }) => setData(d)).catch(() => {});
    };
    fetch();
    const interval = setInterval(fetch, 300000); // 5 min
    return () => clearInterval(interval);
  }, [user]);

  const noMacro = !data || (!data.vix?.level && !data.spy_regime?.state);

  const vix = data?.vix || {};
  const spy = data?.spy_regime || {};
  const elevated = data?.elevated;

  return (
    <div className="flex items-center gap-4 px-3 text-[10px] border-b overflow-x-auto whitespace-nowrap" data-testid="macro-bar"
      style={{ height: 32, minHeight: 32, borderColor: "var(--hoip-border)", background: elevated ? "rgba(245,158,11,0.06)" : "var(--hoip-nav)", fontFamily: "JetBrains Mono, monospace" }}>
      {/* Persistent ticker search — first slot, left of VIX */}
      <HeaderTickerSearch />
      {noMacro ? null : (<>
      <span style={{ color: "var(--hoip-divider)" }}>|</span>
      {/* VIX */}
      {vix.level > 0 && (
        <div className="flex items-center gap-1.5 shrink-0">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>VIX</span>
          <span className="font-bold" style={{ color: vix.color }}>{vix.level}</span>
          <span className="px-1 py-0.5 rounded-sm text-[8px] font-bold" style={{ color: vix.color, background: `${vix.color}15` }}>{vix.badge}</span>
          <span style={{ color: vix.change >= 0 ? "#EF4444" : "#10B981" }}>{vix.change >= 0 ? "\u25B2" : "\u25BC"}{Math.abs(vix.change)}</span>
        </div>
      )}
      <span style={{ color: "var(--hoip-divider)" }}>|</span>
      {/* SPY Regime */}
      {spy.state && (
        <div className="flex items-center gap-1.5 shrink-0">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>SPY</span>
          <span className="font-bold" style={{ color: spy.state === "Bullish Trend" ? "#10B981" : spy.state === "Volatile" ? "#F59E0B" : spy.state === "Bearish Trend" ? "#EF4444" : "#3B82F6" }}>
            {spy.state} {spy.prob}%
          </span>
          {spy.caution && <span className="text-[8px]" style={{ color: "#F59E0B" }}>Caution</span>}
        </div>
      )}
      <span style={{ color: "var(--hoip-divider)" }}>|</span>
      {/* Sector strip */}
      {(data.sector_strip || []).map(s => (
        <span key={s.ticker} className="shrink-0">
          <span style={{ color: "var(--hoip-text-tertiary)" }}>{s.ticker}</span>{" "}
          <span style={{ color: s.change_pct >= 0 ? "#10B981" : "#EF4444" }}>{s.change_pct >= 0 ? "+" : ""}{s.change_pct}%</span>
        </span>
      ))}
      {/* Earnings this week */}
      {data.earnings_this_week?.length > 0 && (
        <>
          <span style={{ color: "var(--hoip-divider)" }}>|</span>
          <button onClick={() => setShowEarnings(!showEarnings)} className="flex items-center gap-1 shrink-0 cursor-pointer hover:underline" style={{ color: "#F59E0B" }}>
            <Activity className="w-3 h-3" />
            {data.earnings_this_week.length} earnings this week
          </button>
          {showEarnings && <span style={{ color: "#F59E0B" }}>{data.earnings_this_week.join(", ")}</span>}
        </>
      )}
      </>)}
    </div>
  );
}

function DataSourceBanner() {
  const [status, setStatus] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      // Iter172.d — must include the auth cookie so the backend can
      // resolve the user's linked broker (Tradier/Alpaca). Without
      // credentials the endpoint hits the anonymous-fallback branch and
      // returns the yfinance delayed banner.
      const { data } = await api.get("/status/data-source");
      setStatus(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (!status) return null;

  const healthPill = <DataHealthAlert health={status.health} />;

  // Broker-linked (Tradier / Alpaca) — show the active source so users
  // know which vendor they're trading off, not a generic "Yahoo (preview)".
  if (status.source === "tradier" || status.source === "alpaca") {
    const accent = status.source === "tradier" ? "#00C896" : "#f59e0b";
    const label = status.source === "tradier" ? "Tradier" : "Alpaca";
    return (
      <div data-testid="data-source-banner"
        className="flex items-center gap-2 px-3 py-1.5 text-xs border-b"
        style={{ background: "var(--hoip-nav)", borderColor: "var(--hoip-border)", color: accent }}>
        {healthPill}
        <CheckCircle className="w-3.5 h-3.5" />
        <span data-testid="data-source-label">Data: {label}{status.env ? ` · ${status.env}` : ""}</span>
        <span className="ml-1 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>(per-component fallbacks shown on each ticker)</span>
      </div>
    );
  }

  if (status.source === "yfinance") {
    return (
      <div data-testid="data-source-banner"
        className="flex items-center gap-2 px-3 py-1.5 text-xs border-b"
        style={{ background: "var(--hoip-nav)", borderColor: "var(--hoip-border)", color: "var(--hoip-text-tertiary)" }}>
        {healthPill}
        <BarChart3 className="w-3.5 h-3.5" />
        <span data-testid="data-source-label">House market data — not live</span>
        <span className="ml-1 text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
          — connect a broker for live data
        </span>
      </div>
    );
  }

  if (status.source === "ibkr" && status.connected) {
    return (
      <div data-testid="data-source-banner"
        className="flex items-center gap-2 px-3 py-1.5 text-xs border-b"
        style={{ background: "var(--hoip-nav)", borderColor: "var(--hoip-border)", color: "#22c55e" }}>
        <CheckCircle className="w-3.5 h-3.5" />
        <span>IBKR Live Connected</span>
      </div>
    );
  }

  // ibkr but not connected
  return (
    <div data-testid="data-source-banner"
      className="flex items-center gap-2 px-3 py-1.5 text-xs border-b"
      style={{ background: "rgba(239,68,68,0.12)", borderColor: "rgba(239,68,68,0.3)", color: "#ef4444" }}>
      <AlertTriangle className="w-3.5 h-3.5" />
      <span>IB Gateway not running — start Gateway on port 7497</span>
    </div>
  );
}

function useIBKRPending() {
  const [pending, setPending] = useState([]);
  const fetch_ = useCallback(async () => {
    try {
      const { data } = await api.get("/ibkr/pending-positions");
      setPending(data.positions || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    fetch_();
    const iv = setInterval(fetch_, 5000);
    return () => clearInterval(iv);
  }, [fetch_]);
  return { pending, refresh: fetch_ };
}

function PendingPositionsBanner({ pending, onRefresh }) {
  const [confirmId, setConfirmId] = useState(null);
  const [entryDate, setEntryDate] = useState("");
  const [loading, setLoading] = useState(false);

  if (!pending.length) return null;

  const handleConfirm = async () => {
    if (!entryDate) return;
    setLoading(true);
    try {
      await api.post("/ibkr/confirm-position", { pending_id: confirmId, entry_date: entryDate });
      setConfirmId(null);
      setEntryDate("");
      onRefresh();
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleDismiss = async (id) => {
    try {
      await api.post("/ibkr/dismiss-position", { pending_id: id });
      onRefresh();
    } catch (e) { console.error(e); }
  };

  return (
    <div data-testid="ibkr-pending-banner"
      className="border-b px-3 py-2"
      style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.25)" }}>
      <div className="flex items-center gap-2 text-xs mb-1.5" style={{ color: "#f59e0b" }}>
        <Radio className="w-3.5 h-3.5" />
        <span className="font-medium">New IBKR position{pending.length > 1 ? "s" : ""} detected — confirm entry date</span>
      </div>
      <div className="space-y-1.5">
        {pending.map(p => (
          <div key={p.id} className="flex items-center gap-3 text-xs rounded px-2 py-1.5"
            style={{ background: "var(--hoip-hover)" }}>
            <span className="font-mono font-medium" style={{ color: "var(--hoip-text-primary)" }}>
              {p.symbol}
            </span>
            <span style={{ color: "var(--hoip-text-secondary)" }}>
              {p.sec_type === "OPT"
                ? `${p.quantity < 0 ? "Short" : "Long"} ${p.right === "P" ? "Put" : "Call"} ${p.strike} ${p.expiry}`
                : `${p.quantity} shares`}
            </span>
            <span style={{ color: "var(--hoip-text-tertiary)" }}>@ ${p.avg_cost}</span>
            <div className="ml-auto flex items-center gap-2">
              {confirmId === p.id ? (
                <>
                  <Input data-testid={`entry-date-${p.id}`} type="date" value={entryDate}
                    onChange={e => setEntryDate(e.target.value)}
                    className="h-6 text-xs w-32" style={{ background: "var(--hoip-bg)" }} />
                  <Button data-testid={`confirm-btn-${p.id}`} size="sm"
                    className="h-6 text-xs px-2" disabled={!entryDate || loading}
                    onClick={handleConfirm}>
                    {loading ? "..." : "Save"}
                  </Button>
                  <Button data-testid={`cancel-btn-${p.id}`} variant="ghost" size="sm"
                    className="h-6 text-xs px-2" onClick={() => setConfirmId(null)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button data-testid={`set-date-btn-${p.id}`} size="sm"
                    className="h-6 text-xs px-2" onClick={() => { setConfirmId(p.id); setEntryDate(""); }}>
                    Set Entry Date
                  </Button>
                  <Button data-testid={`dismiss-btn-${p.id}`} variant="ghost" size="sm"
                    className="h-6 text-xs px-2 opacity-60 hover:opacity-100"
                    onClick={() => handleDismiss(p.id)}>
                    Dismiss
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Iter199 — Sidebar reorganization: Core tier / Pro tier (greyed + locked
// for non-Pro, incl. demo — upsell visibility) / Account section.
const navSections = [
  {
    key: "core",
    label: null,
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      { to: "/portfolio", icon: Briefcase, label: "Portfolio" },
      { to: "/position-management", icon: ClipboardList, label: "Position Management" },
      { to: "/wheel-strategies", icon: TrendingUp, label: "Wheel Income" },
      { to: "/trade-log", icon: BookOpen, label: "Trade Log" },
      { to: "/scanner", icon: ScanSearch, label: "Scanner" },
      { to: "/shadow-money", icon: Landmark, label: "Shadow Money" },
      { to: "/ticker-talk", icon: MessageSquare, label: "Ticker Talk" },
      { to: "/backtest", icon: FlaskConical, label: "Backtest Lab" },
      { to: "/how-to", icon: HelpCircle, label: "How-To" },
      { to: "/settings", icon: Settings, label: "Settings" },
    ],
  },
  {
    key: "account",
    label: "ACCOUNT",
    items: [
      { to: "/profile", icon: UserCircle, label: "Profile" },
      { to: "/billing", icon: CreditCard, label: "Plans" },
    ],
  },
];

const adminItems = [
  { to: "/admin", icon: Users, label: "Admin Panel" },
];

// Data Health Alert — inline pill in the data-source row, LEFT of the
// "Data: …" label (user-specified placement). Hidden when healthy;
// amber = degraded, red = outage. Fed by /status/data-source `health`.
function DataHealthAlert({ health }) {
  if (!health || health.level === "ok") return null;
  const red = health.level === "outage";
  const color = red ? "#EF4444" : "#F59E0B";
  return (
    <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold shrink-0"
      data-testid="data-health-alert"
      title={`Data pipeline ${health.level}: ${health.detail || "elevated provider errors"} (last ${health.window_min}m — ${health.recent_errors}/${health.recent_calls} calls failed)`}
      style={{ color, borderColor: `${color}55`, background: `${color}14` }}>
      <AlertTriangle className="w-3 h-3" />
      {red ? "DATA OUTAGE" : "DATA DEGRADED"}{health.detail ? ` — ${health.detail}` : ""}
    </span>
  );
}

// Persistent ticker search — first slot of the header macro bar, left of VIX.
// Submit validates then deep-links to /ticker/{SYM} (Scanner workbench).
function HeaderTickerSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const sym = q.trim().toUpperCase();
    if (!sym || busy) return;
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/ticker/${encodeURIComponent(sym)}/validate`);
      if (data.valid) {
        setQ(""); setBusy(false);
        navigate(`/ticker/${sym}`);
        return;
      }
      setQ("");
      setErr(`${sym} not found`);
    } catch {
      setQ("");
      setErr("Lookup failed");
    }
    setBusy(false);
  };

  return (
    <form onSubmit={submit} className="flex items-center gap-1 shrink-0" data-testid="header-ticker-search">
      <Search className="w-3 h-3 flex-shrink-0" style={{ color: "var(--hoip-text-tertiary)" }} />
      <input value={q}
        onChange={(e) => { setQ(e.target.value); setErr(""); }}
        onKeyDown={(e) => { if (e.key === "Escape") { setQ(""); setErr(""); } }}
        placeholder={err || (busy ? "Checking…" : "Search ticker…")}
        title={err ? `${err} or unsupported symbol` : "Type a ticker and press Enter — opens its Scanner deep-dive"}
        data-testid="scanner-search-input"
        className="w-[110px] h-[20px] px-1.5 text-[10px] bg-transparent outline-none border rounded-sm uppercase placeholder:normal-case"
        style={{
          borderColor: err ? "#EF4444" : "var(--hoip-border)",
          color: "var(--hoip-text-primary)",
          fontFamily: "'JetBrains Mono', monospace",
        }} />
    </form>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pending, refresh: refreshPending } = useIBKRPending();

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handler = () => { if (window.innerWidth >= 768) setMobileOpen(false); };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebarContent = (
    <>
      <div className="flex items-center h-12 md:h-14 px-3 border-b" style={{ borderColor: "var(--hoip-border)" }}>
        {(!collapsed || mobileOpen) && (
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-500" />
            <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif", color: "var(--hoip-text-primary)" }}>MarkovLab</span>
          </div>
        )}
        {/* Desktop collapse */}
        <Button variant="ghost" size="sm" className="ml-auto p-1 h-7 w-7 hidden md:flex"
          onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-toggle">
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
        {/* Mobile close */}
        <Button variant="ghost" size="sm" className="ml-auto p-1 h-7 w-7 md:hidden"
          onClick={() => setMobileOpen(false)} data-testid="mobile-close-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <nav className="flex-1 py-2 overflow-y-auto relative z-10">
        {/* Iter183a — pinned Portfolio Risk Health LED (visible from every tab). */}
        <div className="mx-2 mb-1 pb-1.5 border-b" style={{ borderColor: "var(--hoip-divider)" }}>
          <RiskHealthLED collapsed={collapsed && !mobileOpen} />
        </div>
        {navSections.map((section) => {
          const items = section.key === "account" && user?.role === "admin"
            ? [...section.items, ...adminItems]
            : section.items;
          return (
          <div key={section.key}>
            {section.label && (
              <div className="mx-2 mt-2 mb-1 pt-2 border-t flex items-center gap-2"
                style={{ borderColor: "var(--hoip-divider)" }}>
                {(!collapsed || mobileOpen) && (
                  <span className="px-2 text-[9px] font-bold tracking-[0.2em]"
                    style={{ color: "var(--hoip-text-tertiary)" }}
                    data-testid={`nav-section-${section.key}`}>{section.label}</span>
                )}
              </div>
            )}
            {items.map(({ to, icon: Icon, label }) => {
          return (
          <NavLink key={to} to={to} end={to === "/"}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 mx-2 px-2 py-2 rounded-sm text-sm transition-colors duration-100 relative z-10 ${
                isActive ? "bg-[var(--hoip-hover)] text-[var(--hoip-text-primary)]"
                  : "text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)] hover:bg-[var(--hoip-hover)]"
              }`}>
            <Icon className="w-4 h-4 flex-shrink-0" />
            {(!collapsed || mobileOpen) && <span>{label}</span>}
            {label === "Portfolio" && pending.length > 0 && (
              <span data-testid="ibkr-pending-badge"
                className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold leading-none px-1"
                style={{ background: "#f59e0b", color: "#000" }}>
                {pending.length}
              </span>
            )}
          </NavLink>
          );
            })}
          </div>
          );
        })}
      </nav>

      <div className="p-2 border-t" style={{ borderColor: "var(--hoip-border)" }}>
        {(!collapsed || mobileOpen) && (
          <p className="px-2 pb-2 text-[8px] leading-snug" style={{ color: "var(--hoip-text-tertiary)" }}
            data-testid="sidebar-compliance-note">
            For informational and educational purposes only. Not investment advice. Not a recommendation to buy,
            sell, or hold any security.
          </p>
        )}
        {(!collapsed || mobileOpen) && (
          <div className="px-2 py-1 mb-2">
            <p className="text-xs truncate" style={{ color: "var(--hoip-text-secondary)" }}>
              {user?.display_name || user?.username}
            </p>
            <p className="text-xs truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{user?.role}</p>
            <div className="mt-2"><ClerkAuthControls compact /></div>
          </div>
        )}
        <Button variant="ghost" size="sm"
          className="w-full justify-start gap-2 text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)] hover:bg-[var(--hoip-hover)]"
          onClick={toggleTheme} data-testid="theme-toggle-btn">
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {(!collapsed || mobileOpen) && (theme === "dark" ? "Light Mode" : "Dark Mode")}
        </Button>
        <Button variant="ghost" size="sm"
          className="w-full justify-start gap-2 text-[var(--hoip-text-secondary)] hover:text-[var(--hoip-text-primary)] hover:bg-[var(--hoip-hover)]"
          onClick={handleLogout} data-testid="logout-btn">
          <LogOut className="w-4 h-4" />
          {(!collapsed || mobileOpen) && "Logout"}
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "var(--hoip-bg)" }}>
      {/* Mobile header bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex md:hidden items-center h-12 px-3 border-b"
        style={{ background: "var(--hoip-nav)", borderColor: "var(--hoip-border)" }}>
        <Button variant="ghost" size="sm" className="p-1 h-8 w-8" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2 ml-2">
          <Activity className="w-4 h-4 text-emerald-500" />
          <span className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>MarkovLab</span>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <aside className="absolute left-0 top-0 bottom-0 w-64 flex flex-col"
            style={{ background: "var(--hoip-nav)" }} onClick={(e) => e.stopPropagation()}>
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside data-testid="sidebar-nav"
        className="hidden md:flex flex-col border-r transition-all duration-150"
        style={{ width: collapsed ? 56 : 220, background: "var(--hoip-nav)", borderColor: "var(--hoip-border)" }}>
        {sidebarContent}
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pt-12 md:pt-0 flex flex-col" style={{ background: "var(--hoip-bg)" }}>
        <MacroBar />
        <TrialCountdownBanner />
        <BrokerConnectBanner />
        <ReConsentBanner />
        <DataSourceBanner />
        <PendingPositionsBanner pending={pending} onRefresh={refreshPending} />
        <div className="flex-1">
          <Outlet key={location.pathname} />
        </div>
        <PortfolioAssistant />
        <footer className="px-3 md:px-6 py-3 border-t text-[10px] leading-snug flex flex-wrap items-center justify-between gap-2" data-testid="app-footer-disclaimer"
          style={{ borderColor: "var(--hoip-divider)", color: "var(--hoip-text-tertiary)", background: "var(--hoip-card)" }}>
          <span className="max-w-3xl">
            MarkovLab is for informational and educational purposes only. Not investment advice. Not a recommendation to
            buy, sell, or hold any security. No content is tailored to any specific user. Users are solely
            responsible for their own investment decisions and should consult a qualified adviser regarding
            their specific circumstances.
          </span>
          <span className="flex items-center gap-3 opacity-80">
            <a href="/profile" className="hover:opacity-100" data-testid="footer-profile-link">Account</a>
            <a href="/legal/terms" className="hover:opacity-100" data-testid="footer-terms-link">Terms</a>
            <a href="/legal/risk" className="hover:opacity-100" data-testid="footer-risk-link">Risk</a>
            <a href="/legal/privacy" className="hover:opacity-100" data-testid="footer-privacy-link">Privacy</a>
            <a href="/legal/refund" className="hover:opacity-100" data-testid="footer-refund-link">Refund</a>
          </span>
        </footer>
      </main>
    </div>
  );
}
